import React from 'react';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';
import { Skeleton } from '@mantine/core';
import { useVirtualizer } from '@tanstack/react-virtual';

import { FilesSearchResult } from 'types/files_search_result';
import { LumeoFileWithSearchMeta } from 'types/file_with_search_meta';

import { FileSearchMetadata } from 'files/components/FileSearchMetadata';
import {
  CursorPagination,
  CursorPaginationProps,
} from 'components/Pagination/CursorPagination';
import { SkeletonList } from 'components/Skeleton/SkeletonList';
import { Text } from 'components/Text/Text';
import { Thumbnail } from 'components/Thumbnail/Thumbnail';
import { useTrackableNames } from 'trackables/hooks';

import styles from './FilesSearchDrawer.module.scss';

const c = classNames.bind(styles);

const overscan = 20;
const estimateSize = 150; // Accommodate events, trackables, and texts

type FilesSearchDrawerListProps = CursorPaginationProps & {
  data: (FilesSearchResult | LumeoFileWithSearchMeta)[];
  isLoading: boolean;
  isFetching: boolean;
  isPreviousData: boolean;
  onFileClick: React.Dispatch<React.SetStateAction<string | undefined>>;
  selectedFileId: string | undefined;
};

export function FilesSearchDrawerList({
  data,
  isLoading,
  isFetching,
  isPreviousData,
  onFileClick,
  selectedFileId,
  ...paginationProps
}: FilesSearchDrawerListProps) {
  const hasScrolled = React.useRef(false);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: data.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateSize,
    overscan,
  });

  const [deletedIds, setDeletedIds] = React.useState<Record<string, true>>({});

  const selectedFileIndex = data.findIndex((file) =>
    'file_id' in file
      ? file.file_id === selectedFileId
      : file.id === selectedFileId
  );

  React.useEffect(() => {
    if (hasScrolled.current || !selectedFileIndex || selectedFileIndex < 0) {
      return;
    }

    virtualizer.scrollToIndex(selectedFileIndex, {
      align: 'start',
      behavior: 'smooth',
    });
    hasScrolled.current = true;
  }, [selectedFileIndex, virtualizer]);

  const trackableIds = React.useMemo(() => {
    const ids = new Set<string>();

    for (const item of data) {
      const trackables =
        'trackables' in item && Array.isArray(item.trackables)
          ? item.trackables
          : [];
      for (const id of trackables) {
        if (id) {
          ids.add(id);
        }
      }
    }

    return Array.from(ids);
  }, [data]);

  const trackableNamesQuery = useTrackableNames(trackableIds);
  const trackableItemsLookup = trackableNamesQuery.data?.items;
  const trackableListsLookup = trackableNamesQuery.data?.lists;

  if (data.length === 0 && !isLoading) {
    return <div className={c('files', 'empty')}>No results found.</div>;
  }

  return (
    <div ref={scrollRef} className={c('files')}>
      {isLoading || (isFetching && isPreviousData) ? (
        <SkeletonList
          component={SkeletonFile}
          min={data.length > 0 ? data.length : 3}
          max={data.length > 0 ? data.length : 3}
        />
      ) : (
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualizer.getVirtualItems().map(({ key, size, start, index }) => {
            const item = data[index];

            const isFileSearchResult = 'file_id' in item;
            const id = isFileSearchResult ? item.file_id : item.id;
            const objects = isFileSearchResult
              ? item.objects
              : item.objects || [];
            const events = isFileSearchResult ? item.events : item.events || [];
            const texts = isFileSearchResult ? item.texts : item.texts || [];
            const trackables = Array.isArray(item.trackables)
              ? item.trackables
              : [];
            const searchScore = item.search_score;
            const isItemDeleted = Boolean(deletedIds[id]);

            return (
              <button
                key={key}
                className={c('file', {
                  selected: selectedFileId === id,
                  deleted: isItemDeleted,
                })}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: `${size}px`,
                  transform: `translateY(${start}px)`,
                }}
                onClick={() => onFileClick(id)}
                disabled={isLoading}
              >
                <div className={`${c('thumbnail')} relative`}>
                  <Thumbnail
                    thumbnail_url={item.thumbnail_url}
                    mime_type={item.mime_type}
                    onError={() =>
                      setDeletedIds((previous) =>
                        previous[id]
                          ? previous
                          : {
                              ...previous,
                              [id]: true,
                            }
                      )
                    }
                  />
                  {searchScore !== null &&
                    searchScore !== undefined &&
                    searchScore > 0 && (
                      <span className="absolute right-1 bottom-1 rounded bg-black/70 px-1 py-0.5 text-xs font-medium text-white">
                        {Math.round(searchScore * 100)}%
                      </span>
                    )}
                </div>
                <Text className={c('timestamp')} size="xsmall" align="start">
                  {DateTime.fromISO(item.created_at).toLocaleString(
                    DateTime.DATETIME_MED
                  )}
                </Text>
                <FileSearchMetadata
                  layout="compact"
                  withoutContainer
                  maxObjectIcons={3}
                  objects={objects}
                  events={events}
                  texts={texts}
                  trackables={trackables}
                  trackableItemsLookup={trackableItemsLookup}
                  trackableListsLookup={trackableListsLookup}
                  sectionClassNames={{
                    objects: c('objects'),
                    events: c('events'),
                    texts: c('texts'),
                    trackables: c('trackables'),
                  }}
                />
              </button>
            );
          })}
        </div>
      )}

      <CursorPagination className={c('pagination')} {...paginationProps} />
    </div>
  );
}

const SkeletonFile = (
  <div style={{ height: estimateSize }} className={c('file', 'loading')}>
    <Thumbnail className={c('thumbnail')} isLoading />
    <Skeleton height="2rem" width="70%" className={c('timestamp')} visible />
    <Skeleton height="1.25rem" width="100%" className={c('objects')} visible />
    <Skeleton
      height="1.25rem"
      width="60%"
      className={c('trackables')}
      visible
    />
  </div>
);
